import { ChangeDetectionStrategy, Component, Input, OnChanges, SimpleChanges, inject } from '@angular/core';
import { MatCheckboxChange } from '@angular/material/checkbox';
import _ from 'lodash';
import { Inventory, InventoryMeta } from '../../inventory.model';
import { InventoryHelper } from '../../inventory.helper';
import { PublishInventoryChangesetHelper } from '../../publish-inventory/publish-inventory-changeset.helper';
import { ModalNotificationService } from 'src/app/notification/modal-notification.service';
import { InventoryContext } from '../../inventory.context';
import { ConnectInventoryVersionControlDialogService } from '../../connect-inventory-version-control/connect-inventory-version-control-dialog.service';
import { InventoryService } from '../../inventory.service';
import { catchError, map } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { Revision } from 'src/app/version-control/revision/revision.model';

@Component({
    selector: 'adm4-inventory-version-control-information',
    template: `
    <div class="section-title">Git Connection</div>
    @if (isVersionedInventory) {
      <table class='settings-info-table'>
        <tr>
          <td>
            @if (inventory.repository) {
              <div class="key"> Repository</div>
              {{ inventory.repository }}
            }
          </td>
          <td>
            @if (inventory.path) {
              <div class="key"> Directory in repository:</div>
              {{ inventory.path }}
            }
          </td>
          <td>
            @if (inventory.branch) {
              <div class="key"> Branch</div>
              {{ inventory.branch }}
            }
          </td>
          <td>
            @if (commitId) {
              <div class='key'>Commit</div>
              <span [ngbTooltip]='inventoryPublishStatus' class='commit-info'>
                <span>{{ commitId }}</span>
                @if (inventoryHasChanges) {
                  <em class="fa fa-info-circle help-icon" aria-hidden="true"></em>
                }
              </span>
            }
          </td>
          @if ((tags$ | async); as tags) {
            @if (tags.length > 0) {
              <td id="inventory-info-tags">
                <div class='key'>Tags</div>
                <span>{{ tags.join(', ') }}</span>
              </td>
            }
          }
        <td>
          @if (inventory.repositoryUsername) {
            <div class="key"> Repository username</div>
            <span id="inventory-info-username">{{ inventory.repositoryUsername }}</span>
          }
        </td>
        <td>
          @if (inventory.repositoryPasswordSecretId) {
            <div class="key"> Repository password secret-ID</div>
            <span id="inventory-info-password-secret-id">{{ inventory.repositoryPasswordSecretId }}</span>
          }
        </td>
      </tr>
      </table>
    }
    <div>
      <div class='inventory-actions'>
        <label class='checkbox-label' [ngbTooltip]='getDisabledPublishRequireFlagTooltip(inventoryContext.hasAdminAccess$ | async)'
          [disableTooltip]='canChangePublishRequireFlag(inventoryContext.hasAdminAccess$ | async)' placement='bottom-left'>
          <mat-checkbox [disabled]="!canChangePublishRequireFlag(inventoryContext.hasAdminAccess$ | async)"
          [checked]="publishRequired" (change)='onPublishRequiredChange($event)'></mat-checkbox>
          <span>Require a published project and inventory for deployment. This will also only allow the deployment to be done in the deployment wizard and not in the background.</span>
        </label>
        @if (!isVersionedInventory) {
          <span ngbTooltip="You don't have permission to change Git settings on this project." [disableTooltip]="hasInventoryModifyPermission" placement="left">
            <button type="button" class="admn4-button-ellipse-blue" [class.cursor-disabled]="!hasInventoryModifyPermission"
              [disabled]="!hasInventoryModifyPermission" (click)="openConnectInventoryToVersionControlDialog()">
              <span class="menu-item-text">Connect to Git</span>
            </button>
          </span>
        }
      </div>
    </div>
    `,
    styleUrls: [
        '../inventory-information-common.scss',
        '../../../common/styles/component-specific/settings-info-table.scss'
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class InventoryVersionControlInformationComponent implements OnChanges {
  private modalNotificationService = inject(ModalNotificationService);
  inventoryContext = inject(InventoryContext);
  private connectInventoryVersionControlDialogService = inject(ConnectInventoryVersionControlDialogService);
  private inventoryService = inject(InventoryService);


  @Input() inventory: Inventory;
  @Input() inventoryMeta: InventoryMeta | null;
  @Input() hasInventoryModifyPermission: boolean;

  publishRequired: boolean;
  tags$: Observable<string[]>;

  public inventoryHasChanges: boolean = false;
  public isVersionedInventory: boolean = false;
  public inventoryPublishStatus: string = '';
  public commitId: string | undefined;

  constructor() {
    this.tags$ = of([]);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.inventory && this.inventory) {
      this.publishRequired = this.inventory.publishRequired;
      this.isVersionedInventory = InventoryHelper.isVersionedInventory(this.inventory);
    }
    if (changes.inventoryMeta) {
      this.inventoryHasChanges = !_.isNil(this.inventoryMeta) && PublishInventoryChangesetHelper.hasLocalChanges(this.inventoryMeta);
      this.inventoryPublishStatus = this.calculateInventoryPublishStatus();
    }
    if (changes.inventory || changes.inventoryMeta) {
      this.commitId = _.isNil(this.inventoryMeta) ? this.inventory.commitId : this.inventoryMeta.localHead;
      if (this.isVersionedInventory && this.inventory.inventoryKey && this.commitId) {
        this.tags$ = this.inventoryService.getInventoryRevision(this.inventory.inventoryKey, this.commitId).pipe(
          map((revision: Revision) => revision?.tags ?? []),
          catchError(() => of([]))
        );
      } else {
        this.tags$ = of([]);
      }
    }
  }

  onPublishRequiredChange(event: MatCheckboxChange): void {
    const checked: boolean = event.checked;
    const title = checked ? 'Enable: deployment requires  published project' : 'Disable: deployment requires  published project';
    const description = checked
      ? 'Enable this setting to allow deployment on this inventory only for published projects.<br/><br/>This will also only allow the deployment to be done in the deployment wizard and not in the background.'
      : 'Disable this setting to allow projects to be deployed on this inventory, regardless if the project is published or not.<br/><br/>This will also allow the deployment to be done in the background, not only on the wizard.';
    const confirmButtonText = checked ? 'Enable' : 'Disable';

    this.modalNotificationService.openConfirmDialog({
      title: title,
      description: description
    }, {
      confirmButtonText: confirmButtonText,
      cancelButtonText: 'Cancel'
    }).afterClosed().subscribe((confirmed?: boolean) => {
      if (confirmed === true) {
        this.inventoryContext.changePublishRequiredFlag(this.inventory.inventoryKey, checked);
      } else {
        event.source.checked = !checked;
      }
    });
  }

  openConnectInventoryToVersionControlDialog() {
    this.connectInventoryVersionControlDialogService.openConnectInventoryToVersionControlDialog(this.inventory);
  }

  canChangePublishRequireFlag(hasAdminAccess: boolean | null): boolean {
    return !!hasAdminAccess && _.isNil(this.inventory.originInventoryKey) && (this.isVersionedInventory || this.inventory.publishRequired);
  }

  getDisabledPublishRequireFlagTooltip(hasAdminAccess: boolean): string {
    if (!hasAdminAccess) {
      return `You don't have permission to modify this setting`;
    } else if (!this.isVersionedInventory && !this.inventory.publishRequired) {
      return 'Please connect this inventory to Git to be able to modify this setting';
    }
    return '';
  }

  private calculateInventoryPublishStatus(): string {
    if (_.isNil(this.inventoryMeta)) {
      return '';
    } else if (this.inventoryHasChanges) {
      return 'There are local changes pending to be published on this inventory';
    } else {
      return 'There are no local changes pending to be published on this inventory';
    }
  }
}
